import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface Category {
  id: string;
  name: string;
  description: string;
  status: string;
  created_at: string | Date;
}

export interface Tag {
  id: string;
  name: string;
  created_at: string | Date;
  category_id: string;
}
@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private categoryUpdate: BehaviorSubject<boolean> = new BehaviorSubject(false);
  categoryUpdate$: Observable<boolean> = this.categoryUpdate.asObservable();
  private categories: Category[] = [];
  private tags: Tag[] = [];

  // Lets components outside the category nav (the mobile tab bar) ask the nav
  // bar to select a category, so the home list refreshes the same way a tap on
  // the nav bar itself would.
  private categorySelection: Subject<{ category: string; option: string }> = new Subject();
  categorySelection$: Observable<{ category: string; option: string }> = this.categorySelection.asObservable();

  // Same idea for the mobile "Search" tab: it has to reach the search input
  // that lives inside the home page.
  private searchFocus: Subject<void> = new Subject();
  searchFocus$: Observable<void> = this.searchFocus.asObservable();
  private searchFocusRequested: boolean = false;

  // The "Search" tab stays lit after the search itself is over, so the tab bar
  // needs a flag of its own rather than deriving the state from the URL.
  private searchTabActive: boolean = false;

  constructor() { }

  private selectedCategory: string = sessionStorage.getItem('selectedCategory') || "";

  setCategoryData(data: any[]): void {
    const that = this;
    data.forEach(function (category: any) {
      category.tags.forEach(function (tag: any) {
        that.tags.push({ 
          id: tag.id, 
          name: tag.name, 
          created_at: tag.created_at, 
          category_id: tag.category_id 
        });
      });

      that.categories.push({
        id: category.id,
        name: category.name,
        status: category.status,
        created_at: category.created_at,
        description: category.description
      });
    });

    this.categoryUpdate.next(true);
  }

  getCategoryData(): Category[] {
    return this.categories;
  }

  getTagsData(): Tag[] {
    return this.tags;
  }

  /**
   * Set selected category
   * @param id 
   */
  setSelectedCategory(id: string): void {
    this.selectedCategory = id;
    if (id) {
      sessionStorage.setItem('selectedCategory', id);
    } else {
      sessionStorage.removeItem('selectedCategory');
    }
  }

  /**
   * Get selected category
   * @returns 
   */
  getSelectedCategory(): string {
    return this.selectedCategory;
  }

  /**
   * Asks the category nav bar to select a category on behalf of another
   * component. Used by the mobile tab bar, which is rendered outside the home
   * page and therefore cannot reach the nav bar directly.
   * @param {string} category - The category id to select.
   * @param {string} [option='dynamic'] - 'default' for "All", 'dynamic' for an API category.
   * @returns {void}
   */
  requestCategorySelection(category: string, option: string = 'dynamic'): void {
    this.categorySelection.next({ category, option });
  }

  /**
   * Asks the home page to focus its market search input. The request is also
   * latched so it survives a navigation to the home page, where the input only
   * exists once the page has been rendered.
   * @returns {void}
   */
  requestSearchFocus(): void {
    this.searchFocusRequested = true;
    this.searchFocus.next();
  }

  /**
   * Reads and clears a pending search focus request.
   * @returns {boolean} True when a focus request was waiting.
   */
  consumeSearchFocusRequest(): boolean {
    const requested: boolean = this.searchFocusRequested;
    this.searchFocusRequested = false;
    return requested;
  }

  /**
   * Marks the mobile tab bar's "Search" tab as the selected one.
   * @param {boolean} active - True while Search is the selected tab.
   * @returns {void}
   */
  setSearchTabActive(active: boolean): void {
    this.searchTabActive = active;
  }

  /**
   * @returns {boolean} True while "Search" is the selected mobile tab.
   */
  isSearchTabActive(): boolean {
    return this.searchTabActive;
  }

}